import { RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ApiError } from '@/services/api'
import type {
  ResolutionBandId,
  ResolutionSummary,
} from '@/services/api/entity-resolution-assistant'
import { selectedResolutionBands } from './entity-resolution'
import { ResolutionBandTable } from './ResolutionBandTable'
import { ResolutionIdentitySummary } from './ResolutionIdentitySummary'
import { ResolutionMergeConfirmation } from './ResolutionMergeConfirmation'
import { ResolutionMergeProgress } from './ResolutionMergeProgress'
import { bulkEligibleGroups, identityChecksAvailable } from './resolution-identity'
import { resolutionJobExpired, useResolutionAssistant } from './useResolutionAssistant'

const COPY = 'resolutionAssistant'
type Refresh = { pending: boolean; run: () => void }

function useResolutionReview(summary: ResolutionSummary, refresh: Refresh) {
  const [selected, setSelected] = useState<ResolutionBandId[]>([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { merge, job, accepted } = useResolutionAssistant(summary.case_id, summary.snapshot_id)
  const bands = selectedResolutionBands(summary, selected)
  const groups = bands.reduce((sum, band) => sum + bulkEligibleGroups(band), 0)
  const needsRefresh = !identityChecksAvailable(summary)
  const toggle = (id: ResolutionBandId, checked: boolean) =>
    setSelected(ids => (checked ? [...new Set([...ids, id])] : ids.filter(value => value !== id)))
  const stale = merge.error instanceof ApiError && merge.error.status === 409
  return {
    bands,
    job,
    selection: {
      ids: selected,
      disabled: accepted || refresh.pending || stale || needsRefresh,
      toggle,
    },
    controls: {
      groups,
      disabled: accepted || refresh.pending || stale || groups === 0,
      error: merge.isError ? (stale ? 'stale' : 'mergeFailed') : null,
      refreshDisabled:
        refresh.pending ||
        merge.isPending ||
        (job.data?.status === 'running' && !resolutionJobExpired(job.error)),
      review: () => setConfirmOpen(true),
    },
    confirmation: {
      open: confirmOpen,
      setOpen: setConfirmOpen,
      confirm: () =>
        merge.mutate({ snapshot_id: summary.snapshot_id, band_ids: bands.map(band => band.id) }),
    },
  }
}

export function ResolutionReview({
  summary,
  refresh,
}: {
  summary: ResolutionSummary
  refresh: Refresh
}) {
  const { t } = useTranslation()
  const review = useResolutionReview(summary, refresh)
  return (
    <>
      <p>
        {t(`${COPY}.totals`, { groups: summary.total_groups, entities: summary.total_candidates })}
      </p>
      <p className="text-xs text-muted-foreground">{t(`${COPY}.basis`)}</p>
      <ResolutionIdentitySummary summary={summary} />
      <ResolutionBandTable summary={summary} selection={review.selection} />
      <p className="text-xs text-muted-foreground">{t(`${COPY}.overlap`)}</p>
      <ResolutionReviewActions controls={review.controls} refresh={refresh.run} />
      <ResolutionMergeProgress
        job={review.job}
        retry={() => {
          void review.job.refetch()
        }}
      />
      <ResolutionMergeConfirmation bands={review.bands} actions={review.confirmation} />
    </>
  )
}

function ResolutionReviewActions({
  controls,
  refresh,
}: {
  controls: ReturnType<typeof useResolutionReview>['controls']
  refresh: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={controls.disabled} onClick={controls.review}>
          {t(`${COPY}.reviewSelection`, { count: controls.groups })}
        </Button>
        <Button variant="outline" size="sm" disabled={controls.refreshDisabled} onClick={refresh}>
          <RefreshCw aria-hidden className="size-3.5" />
          {t(`${COPY}.refresh`)}
        </Button>
      </div>
      {controls.error && (
        <p role="alert" className="text-destructive">
          {t(`${COPY}.${controls.error}`)}
        </p>
      )}
    </>
  )
}
